import type { PropsWithChildren } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Badge } from "@similarweb/ui-components";

import ManagementCardPlaceholder from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/ManagementCardPlaceholder";
import manageIntegrationsTrackingService from "app/pages/sales-intelligence/modules/manage-integrations/services/tracking";
import openExternalLink from "app/pages/sales-intelligence/modules/manage-integrations/utils/openExternalLink";
import {
    INTEGRATION_STATUS,
    SYNC_FAILED_REASON_CODE_INSUFFICIENT_PERMISSIONS,
} from "app/pages/sales-intelligence/pages/management/constants/constants";
import { MANAGEMENT_BODY_TEXTS } from "app/pages/sales-intelligence/pages/management/translations/integrationsKeys";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import integrationTrackingService from "app/pages/sales-intelligence/services/tracking/integrationTrackingService";
import type { IntegrationStatusResponse } from "app/pages/sales-intelligence/sub-modules/management/components/types";
import type { Integration, IntegrationSyncStatus } from "app/pages/sales-intelligence/sub-modules/management/types";

import { useCRMStatusPolling } from "../../../hooks/useCRMStatusPolling";
import { useHasManagementAccess } from "../../../../../pages/management/hooks/useHasManagementAccess";
import { CRM_INTEGRATION_ICON, CRM_INTEGRATION_MANUAL_URLS } from "../../../constants/crm";
import extractCRMData from "../../../utils/extractCRMDataOrPlaceholder";
import { formatLastSyncedLine } from "../../../utils/formatLastSyncedLine";
import { getChipState } from "../../../utils/getChipState";
import ManagementCard from "../../cards-components/ManagementCard";
import { StyledManagementCardFooterWrapper, StyledManagementCardTagWrapper } from "../../cards-components/styles";
import CRMConnectButton from "../../CRMConnectButton/CRMConnectButton";
import IntegrationButtonWrapper from "../../integration-button/IntegrationButtonWrapper";
import { CRMIntegrationCardBody } from "../../integration-cards/CRMIntegrationCardBody/CRMIntegrationCardBody";

export interface IProps {
    integrationName: Integration;
    isLoading?: boolean;
    crmData?: IntegrationStatusResponse;
    onRefetch: () => Promise<IntegrationSyncStatus>;
}

export const CRMIntegrationCard = ({
    integrationName,
    isLoading,
    crmData,
    onRefetch,
    children,
}: PropsWithChildren<IProps>): React.ReactElement => {
    const { translate } = useTranslate();
    const [hasAccess] = useHasManagementAccess(integrationName);

    useCRMStatusPolling({
        syncStatus: crmData?.crmConnectionStatus.syncStatus,
        onRefetch,
        onSettled: (status) =>
            integrationTrackingService.trackStatusConnection(
                integrationName,
                status !== INTEGRATION_STATUS.NotIntegrated,
            ),
    });

    const isCrmIntegrationsEnabled = salesSettingsHelper.isCrmIntegrationsEnabled();

    const handleGoToGateway = () => {
        void siNavigator.goToGatewayPage(integrationName);
    };

    const handleGuideClick = () => {
        if (!CRM_INTEGRATION_MANUAL_URLS[integrationName]) {
            return;
        }

        manageIntegrationsTrackingService.trackCrmGuideClick(integrationName);
        openExternalLink(CRM_INTEGRATION_MANUAL_URLS[integrationName]);
    };

    if (isLoading || !crmData) {
        return <ManagementCardPlaceholder />;
    }

    const {
        isIntegrated,
        isExclusionOn,
        isExportAccountsOn,
        isExportContactsOn,
        isExportLeadsOn,
        activeImportSettings,
        crmConnectionStatus,
    } = extractCRMData(crmData);

    const isInProgress =
        crmConnectionStatus?.syncStatus === INTEGRATION_STATUS.InProgress ||
        crmConnectionStatus?.syncStatus === INTEGRATION_STATUS.New;
    const isReconnectRequired =
        crmConnectionStatus?.syncStatus === INTEGRATION_STATUS.Failed &&
        crmConnectionStatus.syncFailedReasonCode === SYNC_FAILED_REASON_CODE_INSUFFICIENT_PERMISSIONS;
    const hasManagementAccess = hasAccess || (isCrmIntegrationsEnabled && isIntegrated && !isReconnectRequired);
    const lastSyncedLine =
        isInProgress || !crmConnectionStatus ? null : formatLastSyncedLine(crmConnectionStatus.lastSynced, translate);

    const chipState = getChipState(
        crmData.crmConnectionStatus.syncStatus,
        crmData.crmConnectionStatus.syncFailedReasonCode,
        integrationName,
        translate,
    );

    const isDynamicsNotIntegrated =
        integrationName === "dynamics" && crmData.crmConnectionStatus.syncStatus === INTEGRATION_STATUS.NotIntegrated;

    const manageOrUpgradeButton = (
        <IntegrationButtonWrapper
            dataAutomationNameSpace="crm-integration-button"
            featureName={integrationName}
            hasAccess={hasManagementAccess}
            isEnabled={isCrmIntegrationsEnabled}
            onUpgradeClick={handleGoToGateway}
        >
            <CRMConnectButton
                integrationName={integrationName}
                onConnectClick={handleGoToGateway}
                onManage={() => manageIntegrationsTrackingService.trackCrmManage(integrationName)}
                onConnect={() => manageIntegrationsTrackingService.trackCrmConnect(integrationName)}
            />
        </IntegrationButtonWrapper>
    );

    const cardFooter = (
        <StyledManagementCardFooterWrapper>
            {children ? <StyledManagementCardTagWrapper>{children}</StyledManagementCardTagWrapper> : null}
            {manageOrUpgradeButton}
        </StyledManagementCardFooterWrapper>
    );

    return (
        <ManagementCard
            dataAutomation={`${integrationName}-card`}
            title={translate(MANAGEMENT_BODY_TEXTS[integrationName].name)}
            subTitle={lastSyncedLine}
            titleIconName={CRM_INTEGRATION_ICON[integrationName]}
            onHeaderActionClick={
                isIntegrated || !CRM_INTEGRATION_MANUAL_URLS[integrationName] ? undefined : handleGuideClick
            }
            headerBadge={isDynamicsNotIntegrated ? <Badge type="new" /> : undefined}
            {...chipState}
            footer={cardFooter}
        >
            <CRMIntegrationCardBody
                integrationName={integrationName}
                isConnected={isIntegrated}
                statusData={crmConnectionStatus}
                isExclusionOn={isExclusionOn}
                isExportContactsOn={isExportContactsOn}
                isExportLeadsOn={!!isExportLeadsOn}
                isExportAccountsOn={isExportAccountsOn}
                activeImportSettings={activeImportSettings}
            />
        </ManagementCard>
    );
};
